import React, { useEffect, useState } from "react";
import { navLinks, profile } from "../../mock";
import { Menu, X } from "lucide-react";
import { trackEvent } from "../../lib/utils";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("about");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const sections = navLinks.map((l) => document.getElementById(l.id));
      const y = window.scrollY + 140;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.offsetTop <= y) {
          setActive(navLinks[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-300 ${
        scrolled
          ? "bg-[#FAF7F2]/80 backdrop-blur-md border-b border-[#E8E0D3]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => {
            handleClick("top");
            trackEvent("nav_home_click");
          }}
          className="group flex items-center"
          aria-label="Indranil Kargupta — home"
        >
          <span
            className="relative inline-block w-11 h-11 md:w-12 md:h-12 rounded-full overflow-hidden ring-1 ring-[#E8E0D3] shadow-[0_6px_18px_-6px_rgba(31,27,22,0.25)] transition-transform duration-300 group-hover:scale-[1.04]"
            style={{
              backgroundColor: "#FAF7F2",
              backgroundImage: `url(${profile.photo})`,
              backgroundSize: "cover",
              backgroundPosition: "25% 50%",
              backgroundRepeat: "no-repeat",
            }}
          >
            {/* Soft inner vignette to blend into the cream background */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full"
              style={{
                boxShadow:
                  "inset 0 0 0 1px rgba(31,27,22,0.08), inset 0 -8px 18px rgba(250,247,242,0.45)",
              }}
            />
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((l) => (
            <button
              key={l.id}
              onClick={() => {
                handleClick(l.id);
                trackEvent(`nav_${l.id}_click`);
              }}
              className={`relative px-4 py-2 text-sm transition-colors duration-200 ${
                active === l.id
                  ? "text-[#1F1B16]"
                  : "text-[#6B5F52] hover:text-[#1F1B16]"
              }`}
            >
              {l.label}
              {active === l.id && (
                <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-[#1F1B16]" />
              )}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={profile.socials.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("header_linkedin_click")}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#1F1B16] text-[#1F1B16] hover:bg-[#1F1B16] hover:text-[#FAF7F2] transition-colors duration-300 text-sm"
          >
            LinkedIn
            <span aria-hidden>↗</span>
          </a>
          <a
            href="/Resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("header_resume_click")}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#1F1B16] text-[#1F1B16] hover:bg-[#1F1B16] hover:text-[#FAF7F2] transition-colors duration-300 text-sm"
          >
            Resume
            <span aria-hidden>↗</span>
          </a>
        </div>

        <button
          className="md:hidden p-2 text-[#1F1B16]"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#FAF7F2] border-t border-[#E8E0D3]">
          <div className="px-6 py-4 flex flex-col">
            {navLinks.map((l) => (
              <button
                key={l.id}
                onClick={() => {
                  handleClick(l.id);
                  trackEvent(`nav_${l.id}_click`);
                }}
                className="text-left py-3 text-[#1F1B16] border-b border-[#EFE8DA] last:border-0"
              >
                <span className="text-base">{l.label}</span>
              </button>
            ))}
            <div className="mt-4 flex flex-col gap-3">
              <a
                href={profile.socials.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  setOpen(false);
                  trackEvent("header_linkedin_click");
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#1F1B16] text-[#1F1B16] text-sm hover:bg-[#1F1B16] hover:text-[#FAF7F2] transition-colors duration-300"
              >
                LinkedIn <span aria-hidden>↗</span>
              </a>
              <a
                href="/Resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  setOpen(false);
                  trackEvent("header_resume_click");
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#1F1B16] text-[#FAF7F2] text-sm hover:bg-[#2c261f] transition-colors duration-300"
              >
                Resume <span aria-hidden>↗</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
